"use client";

import { AnimatePresence, motion } from "motion/react";
import { Book, ChevronRight, Pencil } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { useProfileEditDialogStore } from "@/features/profile/tutor/store/useProfileEditDialogStore";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";

export function SubjectsDisplay() {
  const { profileData } = useProfileDataStore();
  const data = profileData?.subjects_section;
  const { t } = useTranslation(["profile", "common"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<any>(null);
  const { open } = useProfileEditDialogStore();

  useEffect(() => {
    if (isExpanded && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "instant", block: "start" });
    }
  }, [isExpanded]);
  if (!data) return null;
  const displayedSubjects = isExpanded && data ? data : data.slice(0, 3);
  const hasMore = data.length > 3;

  return (
    <Card
      ref={containerRef}
      className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-l-4 border-none border-l-orange-400/50 shadow-2xl backdrop-blur-xl"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br from-orange-400 to-amber-600" />
        <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-gradient-to-tr from-amber-400 to-orange-500" />
      </div>

      <CardHeader className="relative flex flex-row items-center justify-between pb-2">
        <motion.h2
          animate={{ y: 0, opacity: 1 }}
          className="text-accent-foreground mb-2 flex items-center gap-2 text-xl font-semibold"
          initial={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-center rounded-xl p-2">
            <Icon
              className={"text-orange-400"}
              height={"1.25rem"}
              icon={"mdi:bookshelf"}
              width={"1.25rem"}
            />
          </div>
          {t("profile:subjectsSection.title")}
        </motion.h2>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={
                  "bg-foreground/50 hover:bg-foreground/70 absolute top-2 right-2 rounded-xl backdrop-blur-sm"
                }
                size="sm"
                variant="ghostSecondary"
                onClick={() => open("subjects")}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{t("common:edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CardHeader>
      <CardContent className="relative space-y-6">
        {data.length === 0 && (
          <motion.div
            animate={{ y: 0, opacity: 1 }}
            className="relative h-[120px] w-full space-y-2 rounded-2xl border border-dashed border-orange-300/50 bg-orange-500/5 backdrop-blur-sm"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Button
              className="absolute top-0 left-0 flex h-full w-full items-center rounded-2xl bg-transparent hover:bg-orange-500/10"
              onClick={() => open("subjects")}
            >
              <Pencil className="mr-3 h-6 w-6 text-orange-400" />
              <h3 className="text-accent-foreground text-lg font-medium">
                {t("profile:subjectsSection.add")}
              </h3>
            </Button>
          </motion.div>
        )}
        <AnimatePresence mode={"wait"}>
          {displayedSubjects.map((subject, index) => (
            <motion.div
              key={subject.subject.id}
              animate={{ y: 0, opacity: 1 }}
              className="space-y-2"
              initial={{ y: 10, opacity: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              {index > 0 && <Separator className="my-4 bg-orange-200/20" />}
              <div className="rounded-2xl bg-transparent p-4 backdrop-blur-sm transition-colors hover:bg-orange-500/2">
                <div className="flex items-start space-x-2">
                  <div className="mt-1 flex items-center justify-center rounded-xl p-2">
                    <Icon
                      className={"text-orange-400"}
                      height={"1.25rem"}
                      icon={subject.subject.icon}
                      width={"1.25rem"}
                    />
                  </div>
                  <div className="flex-1 space-y-3">
                    <h3 className="text-accent-foreground text-lg font-semibold">
                      {subject.subject.name}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {subject.curriculumLevels.slice(0, 3).map((level) => (
                        <Badge
                          key={level.id}
                          className="flex items-center gap-2 rounded-xl border-none bg-orange-500/5 px-3 py-1 font-medium text-orange-300 backdrop-blur-sm"
                          variant="outline"
                        >
                          <Book className="h-3 w-3" />
                          {level.name}
                        </Badge>
                      ))}
                      {subject.curriculumLevels.length > 3 && (
                        <Badge
                          className="flex items-center gap-2 rounded-xl border-none bg-orange-500/5 px-3 py-1 font-medium text-orange-300 backdrop-blur-sm"
                          variant="outline"
                        >
                          <Book className="h-3 w-3" />+{subject.curriculumLevels.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {hasMore && (
          <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }} transition={{ delay: 0.2 }}>
            <Button
              className="mt-4 w-full justify-center gap-2 rounded-xl border-none bg-transparent text-orange-300 hover:bg-orange-500/15"
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <span>
                {isExpanded ? t("common:showLess") : t("common:showAll", { count: data.length })}
              </span>
              <ChevronRight
                className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
              />
            </Button>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}
